import { useCallback, useRef, useEffect } from 'react';
import { DateTime } from 'luxon';
import { EVENTS } from 'shared/constants/analytics';

/**
 * Custom hook for handling all bulk actions in the My Content page
 *
 * @param {Object} params - Hook parameters
 * @param {Array} params.selectedRowKeys - Currently selected row keys
 * @param {Array} params.contentItems - Currently loaded content items
 * @param {Function} params.trackActionEvent - Analytics tracking function
 * @param {Function} params.handleBulkAction - Generic bulk action handler
 * @param {Function} params.extendItemExpiration - Function to extend item expiration
 * @param {Function} params.shareItemToHub - Function to share item to hub
 * @param {Function} params.moveItemToCategory - Function to move item to category
 * @param {Function} params.updateItemOwnership - Function to update item ownership
 * @param {Function} params.removeContentItem - Function to remove content item
 * @param {Function} params.setIsMoveModalOpen - Function to set move modal state
 * @param {Function} params.setMoveTarget - Function to set move target
 * @param {Function} params.setOwnershipModalEntity - Function to set ownership modal entity
 * @param {Function} params.setDeleteConfirmation - Function to set delete confirmation
 * @param {Function} params.handleMoveModalClose - Function to close move modal
 * @param {Object} params.lang - Language object
 * @returns {Object} Action handlers
 */
export const useMyContentActions = ({
  selectedRowKeys,
  contentItems,
  trackActionEvent,
  handleBulkAction,
  extendItemExpiration,
  shareItemToHub,
  moveItemToCategory,
  updateItemOwnership,
  removeContentItem,
  setIsMoveModalOpen,
  setMoveTarget,
  setOwnershipModalEntity,
  setDeleteConfirmation,
  handleMoveModalClose,
  lang,
}) => {
  // Use ref to track selectedRowKeys for stable callbacks
  const selectedRowKeysRef = useRef(selectedRowKeys);
  useEffect(() => {
    selectedRowKeysRef.current = selectedRowKeys;
  }, [selectedRowKeys]);

  /**
   * Handles bulk extension of item expiration dates
   * @param {number} yearsToAdd - Number of years to add to expiration
   */
  const handleBulkExtend = useCallback(
    async yearsToAdd => {
      // All selected rows never-expiring → do nothing: no success toast with a
      // zero count, no zero-count analytics event.
      const hasExtendable = selectedRowKeysRef.current.some(
        id => contentItems.find(i => i.id === id)?.expiresAt,
      );
      if (!hasExtendable) return;

      const extendAction = async itemId => {
        const item = contentItems.find(i => i.id === itemId);
        // No stored date = never expires: nothing to extend, stays out of the success count.
        if (!item?.expiresAt) return { skipped: true };
        const newDate = DateTime.fromISO(item.expiresAt).plus({ years: yearsToAdd }).toISO();
        return await extendItemExpiration(itemId, newDate, false);
      };

      await handleBulkAction(
        extendAction,
        lang.BULK_EXTENSION_SUCCESSFUL,
        lang.BULK_EXTENSION_FAILED,
        lang.SUCCESSFULLY_EXTENDED_ITEMS,
        lang.FAILED_TO_EXTEND_ITEMS,
        EVENTS.MY_CONTENT.EXTEND_EXPIRATION,
        { extendByYears: yearsToAdd },
      );
    },
    [contentItems, handleBulkAction, extendItemExpiration, lang],
  );

  /**
   * Handles bulk sharing of items to knowledge network
   */
  const handleBulkShare = useCallback(async () => {
    const shareAction = async itemId => {
      return await shareItemToHub(itemId, true, false);
    };

    await handleBulkAction(
      shareAction,
      lang.BULK_SHARE_SUCCESSFUL,
      lang.BULK_SHARE_FAILED,
      lang.SUCCESSFULLY_SHARED_ITEMS,
      lang.FAILED_TO_SHARE_ITEMS,
      EVENTS.MY_CONTENT.SHARE_TO_NETWORK,
      {},
    );
  }, [handleBulkAction, shareItemToHub, lang]);

  /**
   * Initiates bulk move operation by opening the move modal
   */
  const handleBulkMoveTo = useCallback(() => {
    if (selectedRowKeysRef.current.length === 0) return;
    setIsMoveModalOpen(true);
  }, [setIsMoveModalOpen]);

  /**
   * Creates a function to execute bulk move operation with current moveTarget
   */
  const createBulkMoveHandler = useCallback(
    currentMoveTarget => {
      return async () => {
        if (!currentMoveTarget?.binderId) return;

        const moveAction = async itemId => {
          return await moveItemToCategory(itemId, currentMoveTarget.binderId, false);
        };

        await handleBulkAction(
          moveAction,
          lang.BULK_MOVE_SUCCESSFUL,
          lang.BULK_MOVE_FAILED,
          lang.SUCCESSFULLY_MOVED_ITEMS,
          lang.FAILED_TO_MOVE_ITEMS,
          EVENTS.MY_CONTENT.TRANSFER_ITEM,
          {
            destination: currentMoveTarget.binderId,
          },
        );

        // Close the modal after the move operation
        handleMoveModalClose();
      };
    },
    [handleBulkAction, moveItemToCategory, handleMoveModalClose, lang],
  );

  /**
   * Initiates bulk ownership change by opening the ownership modal
   */
  const handleBulkOwnership = useCallback(() => {
    const keys = selectedRowKeysRef.current;
    if (keys.length === 0) return;
    // Pass array of objects with IDs for bulk ownership update
    const itemsForOwnership = keys.map(id => ({ id }));
    setOwnershipModalEntity(itemsForOwnership);
  }, [setOwnershipModalEntity]);

  /**
   * Initiates bulk delete by opening the delete confirmation modal
   */
  const handleBulkDelete = useCallback(() => {
    const keys = selectedRowKeysRef.current;
    if (keys.length === 0) return;
    // Pass array of objects with IDs for bulk delete confirmation
    const itemsToDelete = keys.map(id => ({ id }));
    setDeleteConfirmation(itemsToDelete);
  }, [setDeleteConfirmation]);

  /**
   * Executes the bulk delete operation using the generic bulk action handler
   */
  const handleBulkDeleteConfirm = useCallback(async () => {
    const deleteAction = async itemId => {
      removeContentItem(itemId);
      return { success: true };
    };

    await handleBulkAction(
      deleteAction,
      lang.BULK_DELETE_SUCCESSFUL,
      lang.BULK_DELETE_FAILED,
      lang.SUCCESSFULLY_DELETED_ITEMS,
      lang.FAILED_TO_DELETE_ITEMS,
      EVENTS.MY_CONTENT.DELETE_ITEM,
      {},
    );
  }, [handleBulkAction, removeContentItem, lang]);

  /**
   * Handles bulk ownership update after user selects new owners
   * @param {Array} _ - Items array (unused, kept for API compatibility)
   * @param {Array} ownerIds - Selected owner IDs
   */
  const handleBulkOwnershipUpdate = useCallback(
    async (_, ownerIds) => {
      const keys = selectedRowKeysRef.current;
      const prevOwnerIds = [
        ...new Set(
          keys.flatMap(id => {
            const item = contentItems.find(i => i.id === id);
            return item?.ownerIds || [];
          }),
        ),
      ];

      const ownershipAction = async itemId => {
        return await updateItemOwnership(itemId, ownerIds, false);
      };

      await handleBulkAction(
        ownershipAction,
        lang.BULK_OWNERSHIP_SUCCESSFUL,
        lang.BULK_OWNERSHIP_FAILED,
        lang.SUCCESSFULLY_UPDATED_OWNERS,
        lang.FAILED_TO_UPDATE_OWNERS,
        EVENTS.MY_CONTENT.CHANGE_OWNER,
        {
          prevOwner: prevOwnerIds.join(','),
          newOwner: ownerIds.join(','),
        },
      );
    },
    [handleBulkAction, updateItemOwnership, lang, contentItems],
  );

  return {
    handleBulkExtend,
    handleBulkShare,
    handleBulkMoveTo,
    createBulkMoveHandler,
    handleBulkOwnership,
    handleBulkDelete,
    handleBulkDeleteConfirm,
    handleBulkOwnershipUpdate,
  };
};
